import { v4 as uuidv4 } from 'uuid'

import {
  getCookie,
  getApexDomain,
  removeToastCookie,
  setToastCookie
} from 'src/lib/js/cookieUtils'
import { detectTrafficSource, TrafficChannel } from './trafficSourceDetector'

const SESSION_TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes
const MAX_SESSION_DURATION_MS = 4 * 60 * 60 * 1000 // 4 hours
const SESSION_COOKIE_NAME = 'toast_ga_session'
const PERSIST_THROTTLE_MS = 30000 // Only update cookie every 30 seconds

export interface TrafficAttribution {
  trafficChannel?: TrafficChannel
  trafficSource?: string
  referrer?: string
}

interface SessionCookieData {
  sessionGuid: string
  sessionStartTime: number
  lastActivityTime: number
  trafficChannel?: TrafficChannel
  trafficSource?: string
  referrer?: string
}

export class SessionManager {
  private sessionGuid: string
  private sessionStartTime: number
  private lastActivityTime: number
  private hasConsent: boolean | null = null
  private lastPersistTime: number = 0
  private isFirstSession: boolean = false
  private trafficChannel?: TrafficChannel
  private trafficSource?: string
  private referrer?: string

  constructor() {
    if (!this.restoreSession()) {
      this.sessionGuid = uuidv4()
      this.sessionStartTime = Date.now()
      this.lastActivityTime = Date.now()
      this.isFirstSession = true
      this.detectAndStoreTrafficSource()
    }
  }

  private detectAndStoreTrafficSource(): void {
    if (typeof window === 'undefined') {
      return
    }

    const trafficData = detectTrafficSource(
      document.referrer,
      window.location.href
    )

    if (trafficData) {
      this.trafficChannel = trafficData.trafficChannel
      this.trafficSource = trafficData.trafficSource
      this.referrer = trafficData.referrer
    }
  }

  private isExpired(): boolean {
    const now = Date.now()
    return (
      now - this.lastActivityTime > SESSION_TIMEOUT_MS ||
      now - this.sessionStartTime > MAX_SESSION_DURATION_MS
    )
  }

  private clearTrafficAttribution(): void {
    this.trafficChannel = undefined
    this.trafficSource = undefined
    this.referrer = undefined
  }

  private resetSession(): void {
    this.sessionGuid = uuidv4()
    this.sessionStartTime = Date.now()
    this.lastActivityTime = Date.now()
    this.isFirstSession = true
    this.clearTrafficAttribution()
    this.detectAndStoreTrafficSource()
    this.persistSession(true)
  }

  private persistSession(force: boolean = false): void {
    if (typeof window === 'undefined') {
      return
    }

    // Only persist if consent has been granted
    if (this.hasConsent !== true) {
      return
    }

    // Throttle persistence unless forced (e.g., on resetSession)
    const now = Date.now()
    if (!force && now - this.lastPersistTime < PERSIST_THROTTLE_MS) {
      return
    }

    const sessionData: SessionCookieData = {
      sessionGuid: this.sessionGuid,
      sessionStartTime: this.sessionStartTime,
      lastActivityTime: this.lastActivityTime
    }

    // Only store traffic attribution if it exists (reduces cookie size)
    if (this.trafficChannel) sessionData.trafficChannel = this.trafficChannel
    if (this.trafficSource) sessionData.trafficSource = this.trafficSource
    if (this.referrer) sessionData.referrer = this.referrer

    const cookieValue = JSON.stringify(sessionData)

    // Set cookie to expire when session expires (30 min after last activity)
    const domain = getApexDomain(window.location.hostname)
    const timeUntilExpiry = SESSION_TIMEOUT_MS - (now - this.lastActivityTime)
    const maxAgeSeconds = Math.floor(timeUntilExpiry / 1000)
    setToastCookie(SESSION_COOKIE_NAME, cookieValue, {
      maxAgeSeconds,
      domain
    })

    this.lastPersistTime = now
  }

  private restoreSession(): boolean {
    if (typeof window === 'undefined') {
      return false
    }

    const cookieValue = getCookie(SESSION_COOKIE_NAME)
    if (!cookieValue) {
      return false
    }

    try {
      const decodedValue = decodeURIComponent(cookieValue)
      const sessionData = JSON.parse(decodedValue) as SessionCookieData
      const now = Date.now()

      if (
        now - sessionData.lastActivityTime > SESSION_TIMEOUT_MS ||
        now - sessionData.sessionStartTime > MAX_SESSION_DURATION_MS
      ) {
        const domain = getApexDomain(window.location.hostname)
        removeToastCookie(SESSION_COOKIE_NAME, domain)
        return false
      }

      this.sessionGuid = sessionData.sessionGuid
      this.sessionStartTime = sessionData.sessionStartTime
      this.lastActivityTime = sessionData.lastActivityTime
      this.trafficChannel = sessionData.trafficChannel
      this.trafficSource = sessionData.trafficSource
      this.referrer = sessionData.referrer

      // Cookie will be refreshed once setConsent(true) is called during tracker initialization
      return true
    } catch (error) {
      console.warn('Failed to restore session from cookie:', error)
      const domain = getApexDomain(window.location.hostname)
      removeToastCookie(SESSION_COOKIE_NAME, domain)
      return false
    }
  }

  updateActivity(): void {
    if (this.isExpired()) {
      this.resetSession()
    } else {
      this.lastActivityTime = Date.now()
      this.persistSession()
    }
  }

  getSessionGuid(): string {
    return this.sessionGuid
  }

  getTimeInSession(): number {
    return Math.floor((Date.now() - this.sessionStartTime) / 1000)
  }

  setConsent(consent: boolean): void {
    this.hasConsent = consent
    if (consent) {
      // Consent granted - persist current session to cookie (force immediate)
      this.persistSession(true)
    } else {
      // Consent revoked - remove cookie
      if (typeof window !== 'undefined') {
        const domain = getApexDomain(window.location.hostname)
        removeToastCookie(SESSION_COOKIE_NAME, domain)
      }
    }
  }

  isNewSessionStart(): boolean {
    return this.isFirstSession
  }

  getTrafficAttribution(): TrafficAttribution {
    return {
      trafficChannel: this.trafficChannel,
      trafficSource: this.trafficSource,
      referrer: this.referrer
    }
  }
}

let instance: SessionManager | null = null

export function getSessionManager(): SessionManager {
  if (!instance) {
    instance = new SessionManager()
  }
  return instance
}
